// Gunsmith sounds (kits/gunsmith.js plays them). Same format as audio.js's def() calls; all synthesized.
//   awp_scope    1-shot  the scope coming up: a soft mechanical whirr + a lens click
//   awp_breath   1-shot  the breath held at full scope: a low, quiet two-note settle
//   shoot_awp    1-shot  the report: a deep cracking boom with a long tail, heard across the map
//   awp_bolt     1-shot  the heavy bolt cycle: lift, draw back (clack), push home (clack), drop
//   shoot_pumpy  1-shot  a wide, loud, low shotgun blast
//   pumpy_pump   1-shot  the rack: slide back + front clacks, a shell ejecting
//   pumpy_chamber 1-shot  the shell seating
//   shoot_dregs  1-shot  a wet squeeze-out; pitch rises with the power
import { SFX } from '../../audio/audio.js';

const def = (name, o) => { SFX[name] = o; };

def('awp_scope', {
  gain: 0.4, max: 2, jitter: 0.02, reverb: 0.08,
  build(v, p) {
    v.nz({ f: 900 * p, f1: 2400 * p, sw: 0.3, q: 2, a: 0.05, d: 0.2, peak: 0.16 });
    v.tone({ type: 'triangle', f: 180 * p, f1: 320 * p, sw: 0.3, a: 0.04, d: 0.22, peak: 0.12 });
    v.nz({ t: 0.32, f: 3200 * p, q: 3, a: 0.0004, d: 0.012, peak: 0.55 });
  },
});
def('awp_breath', {
  gain: 0.3, max: 2, jitter: 0, reverb: 0.15,
  build(v, p) {
    v.tone({ f: 196 * p, a: 0.01, d: 0.25, peak: 0.22 });
    v.tone({ t: 0.07, f: 147 * p, a: 0.01, d: 0.3, peak: 0.2 });
  },
});
def('shoot_awp', {
  gain: 0.95, max: 3, jitter: 0.02, reverb: 0.32,
  build(v, p) {
    v.nz({ ft: 'highpass', f: 2200, a: 0.0002, d: 0.05, peak: 1 });                                       // crack
    v.tone({ f: 5400 * p, a: 0.0002, d: 0.008, peak: 0.25 });
    v.tone({ f: 95 * p, f1: 28 * p, sw: 0.25, a: 0.001, d: 0.6, peak: 1 });                               // boom
    v.tone({ type: 'triangle', f: 210 * p, f1: 60 * p, sw: 0.1, a: 0.001, d: 0.2, peak: 0.45 });
    v.nz({ kind: 'pink', ft: 'lowpass', f: 2600, f1: 160, sw: 0.5, q: 1, a: 0.002, d: 0.7, peak: 1 });    // long tail
    v.nz({ t: 0.03, f: 1800 * p, f1: 380 * p, sw: 0.5, q: 2.5, a: 0.01, d: 0.6, peak: 0.45 });            // ink streak
    v.nz({ t: 0.06, ft: 'highpass', f: 3000, a: 0.03, d: 0.5, peak: 0.18 });
  },
});
def('awp_bolt', {
  gain: 0.8, max: 3, jitter: 0.02, reverb: 0.2,
  build(v, p) {
    const clack = (t, hard, f) => {
      v.nz({ t, f: 2800 * p, q: 2.2, a: 0.0003, d: 0.012, peak: 0.9 * hard });
      v.tone({ t, f: f * p, f1: f * 0.5 * p, sw: 0.04, a: 0.001, d: 0.06, peak: 0.55 * hard });
      v.tone({ t, f: 1700 * p, a: 0.0005, d: 0.06, peak: 0.12 * hard });
    };
    v.nz({ t: 0.0, f: 1500 * p, f1: 900 * p, sw: 0.1, q: 1.6, a: 0.02, d: 0.1, peak: 0.2 });              // handle lifts
    clack(0.1, 0.7, 260);
    v.nz({ t: 0.22, f: 1100 * p, f1: 1900 * p, sw: 0.3, q: 1.2, a: 0.05, d: 0.28, peak: 0.16 });          // draw back
    clack(0.5, 1, 200);                                                                                    // shell out
    v.tone({ t: 0.6, f: 3300 * p, f1: 1800 * p, sw: 0.05, a: 0.0006, d: 0.18, peak: 0.12 });               // brass tink
    v.nz({ t: 0.78, f: 1900 * p, f1: 1100 * p, sw: 0.2, q: 1.2, a: 0.04, d: 0.15, peak: 0.18 });          // push home
    clack(0.9, 1, 230);
    clack(1.12, 0.6, 330);                                                                                 // handle drops
  },
});
def('shoot_pumpy', {
  gain: 0.8, max: 4, jitter: 0.04, reverb: 0.2,
  build(v, p) {
    v.nz({ ft: 'highpass', f: 2800, a: 0.0003, d: 0.025, peak: 0.9 });
    v.tone({ f: 120 * p, f1: 36 * p, sw: 0.2, a: 0.001, d: 0.36, peak: 1 });
    v.nz({ kind: 'pink', ft: 'lowpass', f: 3200, f1: 240, sw: 0.25, q: 1, a: 0.001, d: 0.3, peak: 1 });
    v.nz({ t: 0.01, f: 1400 * p, f1: 420 * p, sw: 0.16, q: 3, a: 0.004, d: 0.18, peak: 0.6 });
    for (let i = 0; i < 5; i++) v.bub(v.t + 0.03 + i * 0.02 + v.r(0, 0.01), v.r(700, 1700) * p, 0.14, v.r(0.014, 0.03), v.r(1.5, 2));
  },
});
def('pumpy_pump', {
  gain: 0.75, max: 3, jitter: 0.03, reverb: 0.08,
  build(v, p) {
    const clack = (t, hard) => {
      v.nz({ t, f: 3000 * p, q: 2.4, a: 0.0003, d: 0.01, peak: 0.85 * hard });
      v.tone({ t, f: 220 * p, f1: 110 * p, sw: 0.03, a: 0.001, d: 0.04, peak: 0.55 * hard });
      v.tone({ t, f: 1850 * p, a: 0.0005, d: 0.05, peak: 0.12 * hard });
    };
    v.nz({ f: 1200 * p, f1: 2200 * p, sw: 0.1, q: 1.2, a: 0.02, d: 0.08, peak: 0.14 });
    clack(0.1, 0.9);                                                                                       // slide back (shell out)
    v.tone({ t: 0.14, f: 3200 * p, f1: 1700 * p, sw: 0.05, a: 0.0006, d: 0.16, peak: 0.1 });
    v.nz({ t: 0.2, f: 2000 * p, f1: 1300 * p, sw: 0.1, q: 1.2, a: 0.03, d: 0.08, peak: 0.12 });
    clack(0.3, 1);                                                                                         // slide forward (shell in)
  },
});
def('pumpy_chamber', {
  gain: 0.3, max: 2, jitter: 0.03, reverb: 0.04,
  build(v, p) { v.nz({ f: 2400 * p, q: 2, a: 0.0004, d: 0.012, peak: 0.5 }); v.tone({ f: 340 * p, f1: 190 * p, sw: 0.02, a: 0.001, d: 0.03, peak: 0.3 }); },
});
def('shoot_dregs', {
  gain: 0.6, max: 8, jitter: 0.06, reverb: 0.06, minGap: 0.02,
  build(v, p) {
    v.nz({ f: 1400 * p, f1: 500 * p, sw: 0.06, q: 4, a: 0.003, d: 0.09, peak: 0.7 });                     // wet squelch
    v.tone({ f: 140 * p, f1: 70 * p, sw: 0.05, a: 0.001, d: 0.06, peak: 0.45 });
    v.bub(v.t + v.r(0.015, 0.03), v.r(600, 1100) * p, 0.16, 0.025, 1.8);
    v.nz({ ft: 'highpass', f: 4500, a: 0.001, d: 0.04, peak: 0.12 });
  },
});
